import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Link from "next/link";
import Slide from "@material-ui/core/Slide";
import Fade from "@material-ui/core/Fade";
import MuiBox from "@material-ui/core/Box";
import MuiClickAwayListener from "@material-ui/core/ClickAwayListener";

import { useTheme } from "lib/contexts/themeContext";
import useIsResMobile from "lib/hooks/useIsResMobile";
import IconCancel from "lib/assets/cancel.svg";
import useWindowSize from "lib/hooks/useWindowSize";
import { SubdirectoryArrowLeft } from "@material-ui/icons";

interface MenuProps {
  className?: string;
}

/**
 * @example
 *   <Menu>
 *     <MenuItem href="/">Home</MenuItem>
 *     <MenuItem icon={<IconDelete />} href="/">Delete</MenuItem>
 *     <MenuItem onClick={handleOnClick}>Cancel</MenuItem>
 *   </Menu>
 */
export const Menu: React.FC<MenuProps> = ({ className, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { breakpoints } = useTheme();

  const handleOnClick = useCallback(() => {
    setIsOpen((x) => !x);
  }, []);

  const handleOnClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <MuiClickAwayListener onClickAway={handleOnClose}>
        <div
          css={`
            position: relative;
            width: 22px;
            height: 22px;
          `}
        >
          <Button className={className} onClick={handleOnClick}>
            <Dot />
            <Dot />
            <Dot />
          </Button>

          <MenuItems open={isOpen} onClose={handleOnClose}>
            {children}
            <MenuItem
              icon={<IconCancel />}
              css={`
                display: none;
                ${breakpoints.down("sm")} {
                  display: block;
                }
              `}
            >
              Cancel
            </MenuItem>
          </MenuItems>
        </div>
      </MuiClickAwayListener>
    </>
  );
};

export default Menu;

const MENU_ITEM_WIDTH = 213;

interface MenuItemsProps {
  open: boolean;
  onClose: () => void;
  className?: string;
}

const MenuItems: React.FC<MenuItemsProps> = ({
  open,
  onClose,
  className,
  children,
}) => {
  const ref = useRef(null);
  const [posLeft, setPosLeft] = useState(0);
  const { breakpoints, zIndex } = useTheme();
  const isResMobile = useIsResMobile();

  useEffect(() => {
    if (open && ref.current) {
      const windowWidth = window.innerWidth;
      const posRightMost =
        ref.current.getBoundingClientRect().x + MENU_ITEM_WIDTH;
      if (posRightMost >= windowWidth) {
        setPosLeft(windowWidth - posRightMost - 10);
      } else {
        setPosLeft(0);
      }
    }
  }, [open]);

  if (isResMobile) {
    return (
      <>
        <Fade in={open} unmountOnExit>
          <MuiBox
            position="fixed"
            zIndex={zIndex.drawer}
            top={0}
            right={0}
            bottom={0}
            left={0}
            bgcolor="rgba(0, 0, 0, 0.5)"
            onClick={onClose}
          />
        </Fade>

        <MuiBox position="relative" zIndex={zIndex.drawer}>
          <Slide in={open} direction="up" unmountOnExit>
            <MuiBox
              css={`
                position: absolute;
                padding: 10px 0;
                border-top-left-radius: 10px;
                border-top-right-radius: 10px;
                box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
                background-color: white;
                ${breakpoints.down("sm")} {
                  position: fixed;
                  bottom: 0;
                  left: 0;
                  right: 0;
                }
              `}
              className={className}
              zIndex={9}
              onClick={onClose}
            >
              {children}
            </MuiBox>
          </Slide>
        </MuiBox>
      </>
    );
  }

  return (
    <div ref={ref}>
      <Fade in={open} unmountOnExit>
        <MuiBox
          css={`
            position: absolute;
            left: ${posLeft}px;
            padding: 10px 0;
            width: ${MENU_ITEM_WIDTH}px;
            border-radius: 10px;
            box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
            background-color: white;
            ${breakpoints.down("sm")} {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
            }
          `}
          className={className}
          zIndex={9}
          onClick={onClose}
        >
          {children}
        </MuiBox>
      </Fade>
    </div>
  );
};

interface MenuItemProps {
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  href,
  onClick,
  children,
  className,
}) => {
  return (
    <div className={className}>
      {href ? (
        <MenuItemWithHref
          className={className}
          href={href}
          icon={icon}
          onClick={onClick}
        >
          {children}
        </MenuItemWithHref>
      ) : (
        <MenuItemWithNoHref className={className} icon={icon} onClick={onClick}>
          {children}
        </MenuItemWithNoHref>
      )}
    </div>
  );
};

interface MenuItemWithNoHrefProps {
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const MenuItemWithNoHref: React.FC<MenuItemWithNoHrefProps> = ({
  icon,
  onClick,
  className,
  children,
}) => {
  return (
    <button
      css={`
        display: flex;
        align-items: center;
        padding: 0 30px;
        width: 100%;
        height: 60px;
        border: none;
        background: none;
        cursor: pointer;
        line-height: 24px;
        font-size: 16px;
        color: black;
        transition: background-color 150ms ease-out;
        :hover {
          background-color: #f3f4f8;
        }
      `}
      className={className}
      onClick={onClick}
    >
      {icon && (
        <div
          css={`
            margin-right: 20px;
            width: 20px;
            height: 20px;
          `}
          className="MenuItemIcon"
        >
          {icon}
        </div>
      )}
      {children}
    </button>
  );
};

interface MenuItemWithHrefProps {
  href: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const MenuItemWithHref: React.FC<MenuItemWithHrefProps> = ({
  href,
  icon,
  onClick,
  className,
  children,
}) => {
  return (
    <Link href={href}>
      <a
        css={`
          display: flex;
          align-items: center;
          padding: 18px;
          width: 100%;
          height: 60px;
          border: none;
          line-height: 24px;
          font-size: 16px;
          background: none;
          color: black;
          transition: background-color 150ms ease-out;
          :hover {
            background-color: #f3f4f8;
            color: inherit;
          }
        `}
        href={href}
        onClick={onClick}
        className={className}
      >
        {icon && (
          <div
            css={`
              margin-right: 20px;
              width: 20px;
              height: 20px;
            `}
            className="MenuItemIcon"
          >
            {icon}
          </div>
        )}
        {children}
      </a>
    </Link>
  );
};

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  margin: 0;
  width: 22px;
  height: 22px;
  background: none;
  cursor: pointer;
`;

const Dot = () => {
  const {
    palette: { grey },
  } = useTheme();
  return (
    <div
      css={`
        margin-right: 3px;
        width: 3px;
        height: 3px;
        border-radius: 1.5px;
        background-color: ${grey[500]};
        transition: background-color 200ms ease-out;
        :last-of-type {
          margin-right: 0;
        }
        ${Button}:hover & {
          background-color: ${grey[600]};
        }
      `}
      className="menu-dot"
    />
  );
};
